import "../../main.scss"
import "./Fullscreen.scss"
import SideMenu from "../nav/SideMenu"
import ResponsiveAppBar from "../nav/ResponsiveAppBar"
import { ImpersonationBanner } from "../auth/ImpersonationBanner"
import { ThemeProvider, createTheme } from "@mui/material"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useMemo, useState, useRef } from "react"
import { Database, Dot, Menu, Search, User } from "lucide-react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import {
    searchAll,
    SEARCH_PER_COLLECTION_LIMIT,
    type SearchCollectionResult,
} from "../api/searchApi"
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs"
import { searchInputOverlayIconClass } from "@/lib/searchInputIcon"
import { buildCollectionPath, parseCollectionTablePath } from "../utils/collectionPaths"
import { buildCollectionSearchNavigationState } from "../nav/searchNavigationState"
import { confirmLeaveItemEditorIfNeeded } from "../component/itemEditorNavigationGuard"
import { getCollectionCreatorBadgeCopy } from "../utils/collectionCreatorBadge"
import { useAppVersion } from "@/hooks/useAppVersion"
import { QuickAddDialog } from "../inbox/QuickAddDialog"
import { useAuth } from "../auth/AuthContext"
import { loadAutoOpenQuickAddOnDashboard } from "../inbox/inboxQuickAddPrefs"
import { useCreatableCollections } from "../inbox/useCreatableCollections"

declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        xxl: true;
    }
}

const getTheme = (darkMode: boolean) => createTheme({
    palette: {
        mode: darkMode ? 'dark' : 'light',
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 640,
            md: 768,
            lg: 1024,
            xl: 1280,
            xxl: 1536
        },
    }
});

const FullScreenComponent = ({ title: title, slug: slug, children: children }: { title?: string, slug?: string, children: any }) => {
    const darkMode = useSelector((state: any) => state.ui?.darkMode)
    const components = useSelector((state: any) => state.components?.data ?? EMPTY_ARRAY)
    const componentsLoading = useSelector((state: any) => state.components?.loading) || false
    const componentsError = useSelector((state: any) => state.components?.error) || null
    const theme = getTheme(darkMode)
    const location = useLocation()
    const navigate = useNavigate()
    const env = import.meta.env
    const { version: appVersion, releaseDate: appReleaseDate } = useAppVersion()
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [isCollectionSearchOpen, setIsCollectionSearchOpen] = useState(false)
    const [collectionSearchTerm, setCollectionSearchTerm] = useState("")
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)
    const [itemSelectedIndex, setItemSelectedIndex] = useState<number>(-1)
    const highlightedItemRef = useRef<HTMLButtonElement | null>(null)
    const [itemCollections, setItemCollections] = useState<SearchCollectionResult[] | null>(null)
    const [itemsLoading, setItemsLoading] = useState(false)
    const [itemsError, setItemsError] = useState<string | null>(null)
    const [isQuickAddOpen, setIsQuickAddOpen] = useState(false)
    const { user: authUser } = useAuth()
    const autoQuickAddHandledRef = useRef(false)
    const quickAddUserId =
        authUser?.uid ||
        authUser?.info?.uid ||
        authUser?.id ||
        authUser?._id ||
        null
    const { hasCreatableCollections, loading: creatableCollectionsLoading } =
        useCreatableCollections(Boolean(quickAddUserId))
    const canQuickAddRef = useRef(false)
    canQuickAddRef.current = hasCreatableCollections

    useEffect(() => {
        if (location.pathname !== "/") {
            autoQuickAddHandledRef.current = false
            return
        }
        if (creatableCollectionsLoading || !quickAddUserId || autoQuickAddHandledRef.current) {
            return
        }

        autoQuickAddHandledRef.current = true
        if (
            hasCreatableCollections &&
            loadAutoOpenQuickAddOnDashboard(quickAddUserId)
        ) {
            setIsQuickAddOpen(true)
        }
    }, [
        location.pathname,
        quickAddUserId,
        creatableCollectionsLoading,
        hasCreatableCollections,
    ])

    useEffect(() => {
        const term = collectionSearchTerm.trim()

        if (!term) {
            setItemCollections(null)
            setItemsError(null)
            setItemsLoading(false)
            return
        }

        const controller = new AbortController()
        setItemsLoading(true)
        setItemsError(null)

        const timeoutId = window.setTimeout(() => {
            searchAll(term, SEARCH_PER_COLLECTION_LIMIT, controller.signal)
                .then((collections) => {
                    setItemCollections(collections)
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) {
                        return
                    }
                    const message =
                        error instanceof Error && error.message
                            ? error.message
                            : "Failed to search items"
                    setItemsError(message)
                    setItemCollections(null)
                })
                .finally(() => {
                    if (!controller.signal.aborted) {
                        setItemsLoading(false)
                    }
                })
        }, 350)

        return () => {
            window.clearTimeout(timeoutId)
            controller.abort()
        }
    }, [collectionSearchTerm])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const tagName = target?.tagName
            const isTypingElement =
                tagName === "INPUT" ||
                tagName === "TEXTAREA" ||
                target?.isContentEditable

            if (isTypingElement) {
                return
            }

            if (
                event.key === "q" &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey &&
                !event.shiftKey
            ) {
                if (!canQuickAddRef.current) {
                    return
                }
                event.preventDefault()
                setIsQuickAddOpen(true)
            }

            if (
                event.key === "/" &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey &&
                !event.shiftKey
            ) {
                event.preventDefault()
                setIsCollectionSearchOpen(true)
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [])

    const filteredCollections = useMemo(() => {
        if (!components || components.length === 0) return []
        const term = collectionSearchTerm.trim().toLowerCase()
        if (!term) return components
        return components.filter((c: any) => {
            const name = (c.name || "").toLowerCase()
            const slugValue = (c.slug || "").toLowerCase()
            return name.includes(term) || slugValue.includes(term)
        })
    }, [components, collectionSearchTerm])

    const hasCollections = components && components.length > 0

    useEffect(() => {
        if (!isCollectionSearchOpen || filteredCollections.length === 0) {
            setSelectedIndex(-1)
            return
        }
        setSelectedIndex(0)
    }, [isCollectionSearchOpen, filteredCollections.length])

    useEffect(() => {
        // When items are available for the current search term, auto-select the first suggestion
        const flatItems = (itemCollections || []).flatMap((col) =>
            col.results.map((entry) => ({ col, entry }))
        )
        if (!isCollectionSearchOpen || !collectionSearchTerm.trim() || flatItems.length === 0) {
            setItemSelectedIndex(-1)
            return
        }
        setItemSelectedIndex(0)
    }, [isCollectionSearchOpen, collectionSearchTerm, itemCollections])

    useEffect(() => {
        if (!isCollectionSearchOpen) return
        const el = highlightedItemRef.current
        if (el) {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
    }, [itemSelectedIndex, isCollectionSearchOpen])

    const navigateToCollectionAtIndex = (index: number) => {
        if (!filteredCollections || filteredCollections.length === 0) return
        const safeIndex = Math.min(Math.max(index, 0), filteredCollections.length - 1)
        const c = filteredCollections[safeIndex]
        if (!c) return
        const rawSlug = typeof c.slug === "string" ? c.slug : ""
        const slugValue = rawSlug.replace(/[!]+$/, "").trim()
        if (!slugValue) return
        if (!confirmLeaveItemEditorIfNeeded()) return
        setIsCollectionSearchOpen(false)
        setCollectionSearchTerm("")
        setSelectedIndex(-1)
        navigate(buildCollectionPath({ slug: slugValue, organization: c.organization }))
    }

    const path = location.pathname || ""
    const collectionPathCtx = parseCollectionTablePath(path)
    const isCollectionRoute = collectionPathCtx != null || path.startsWith("/component/")
    const isHomeRoute = path === "/"

    let headerTitle: string | undefined = slug || title

    if (collectionPathCtx) {
        headerTitle = collectionPathCtx.slug.replace(/-/g, " ")
    } else if (path.startsWith("/component/")) {
        const rawSlug = path.replace("/component/", "").replace(/[!]+$/, "").trim()
        headerTitle = rawSlug.replace(/-/g, " ")
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col overflow-hidden">
                <ImpersonationBanner />
                <ResponsiveAppBar
                    title={headerTitle}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                />
                <div className="flex-1 flex min-h-0 flex-col xl:flex-row">
                    <div className="hidden xl:block w-1/6 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm h-full overflow-hidden shrink-0">
                        <SideMenu />
                    </div>
                    <div className="block xl:hidden w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shrink-0 flex flex-row items-center px-4 py-3 gap-3">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(true)}
                            className="inline-flex items-center px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                            aria-label="Open navigation menu"
                        >
                            <Menu className="w-4 h-4 mr-1" />
                            <span>Menu</span>
                        </button>
                        {hasCollections && (
                            <button
                                type="button"
                                onClick={() => setIsCollectionSearchOpen(true)}
                                className="flex-1 inline-flex items-center px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                                aria-label="Search collections"
                            >
                                <Search className="w-4 h-4 mr-2 text-gray-400" />
                                <span className="truncate">Search collections</span>
                            </button>
                        )}
                    </div>
                    <div className="flex flex-1 min-h-0 flex-col overflow-hidden dark:bg-gray-900 w-full">
                        <div className="flex flex-1 min-h-0 flex-col w-full h-full overflow-hidden">{children}</div>
                    </div>
                </div>
                <Dialog
                    open={isCollectionSearchOpen}
                    onOpenChange={(open) => {
                        setIsCollectionSearchOpen(open)
                        if (!open) {
                            setCollectionSearchTerm("")
                            setSelectedIndex(-1)
                            setItemSelectedIndex(-1)
                        }
                    }}
                >
                    <DialogContent className="sm:max-w-[520px]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Search className="w-4 h-4" />
                                Search collections
                            </DialogTitle>
                            <DialogDescription>
                                Find and open any collection by name or slug.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 pt-2">
                            <div className="relative flex items-center">
                                <Search className={searchInputOverlayIconClass("left-2.5", "text-gray-400")} />
                                <Input
                                    autoFocus
                                    type="text"
                                    placeholder="Search..."
                                    value={collectionSearchTerm}
                                    onChange={(e) => {
                                        setCollectionSearchTerm(e.target.value)
                                        setItemSelectedIndex(-1)
                                    }}
                                    onKeyDown={(e) => {
                                        const flatItems = (itemCollections || []).flatMap((col) =>
                                            col.results.map((entry) => ({ col, entry }))
                                        )

                                        // If we have item results, arrow keys should drive those first
                                        if (flatItems.length > 0) {
                                            if (e.key === "ArrowDown") {
                                                e.preventDefault()
                                                setItemSelectedIndex((prev) => {
                                                    const base = prev < 0 ? 0 : prev
                                                    return base >= flatItems.length - 1 ? 0 : base + 1
                                                })
                                                return
                                            }
                                            if (e.key === "ArrowUp") {
                                                e.preventDefault()
                                                setItemSelectedIndex((prev) => {
                                                    const base = prev < 0 ? 0 : prev
                                                    return base <= 0 ? flatItems.length - 1 : base - 1
                                                })
                                                return
                                            }
                                            if (e.key === "Enter") {
                                                e.preventDefault()
                                                const index = itemSelectedIndex >= 0 ? itemSelectedIndex : 0
                                                const target = flatItems[index]
                                                if (!target) return
                                                const rawSlug = typeof (target.col.slug || target.col.collection) === "string"
                                                    ? (target.col.slug || target.col.collection)
                                                    : ""
                                                const slugValue = rawSlug.replace(/[!]+$/, "").trim()
                                                if (!slugValue) return
                                                const highlightUid = target.entry.ref || target.entry.item?.uid || null
                                                if (!confirmLeaveItemEditorIfNeeded()) return
                                                setIsCollectionSearchOpen(false)
                                                setCollectionSearchTerm("")
                                                setSelectedIndex(-1)
                                                setItemSelectedIndex(-1)
                                                navigate(buildCollectionPath({ slug: slugValue }), {
                                                    state: buildCollectionSearchNavigationState({
                                                        highlightUid,
                                                        matchingField: target.entry.matchingField,
                                                        matchingValue: target.entry.matchingValue,
                                                        searchQuery: collectionSearchTerm,
                                                    }),
                                                })
                                                return
                                            }
                                        }

                                        // Fallback: navigate collections when there are no item results
                                        if (!filteredCollections || filteredCollections.length === 0) {
                                            return
                                        }
                                        if (e.key === "ArrowDown") {
                                            e.preventDefault()
                                            setSelectedIndex((prev) => {
                                                if (filteredCollections.length === 0) return -1
                                                const next = prev < 0 ? 0 : (prev + 1) % filteredCollections.length
                                                return next
                                            })
                                        } else if (e.key === "ArrowUp") {
                                            e.preventDefault()
                                            setSelectedIndex((prev) => {
                                                if (filteredCollections.length === 0) return -1
                                                const base = prev < 0 ? 0 : prev
                                                const next = (base - 1 + filteredCollections.length) % filteredCollections.length
                                                return next
                                            })
                                        } else if (e.key === "Enter") {
                                            e.preventDefault()
                                            const index = selectedIndex >= 0 ? selectedIndex : 0
                                            navigateToCollectionAtIndex(index)
                                        }
                                    }}
                                    className="pl-8"
                                />
                            </div>
                            {collectionSearchTerm && (
                                <div className="space-y-2">
                                    {itemsLoading && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Searching items...
                                        </p>
                                    )}
                                    {itemsError && !itemsLoading && (
                                        <p className="text-sm text-red-500 dark:text-red-400">
                                            {itemsError}
                                        </p>
                                    )}
                                    {!itemsLoading && !itemsError && itemCollections && itemCollections.length > 0 && (
                                        <div className="space-y-1">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Items
                                            </div>
                                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                                {itemCollections.flatMap((col, colIndex) =>
                                                    col.results.map((entry, entryIndex) => {
                                                        const flatIndex = itemCollections
                                                            .slice(0, colIndex)
                                                            .reduce((sum, c) => sum + c.results.length, 0) + entryIndex
                                                        const rawSlug = typeof (col.slug || col.collection) === "string" ? (col.slug || col.collection) : ""
                                                        const slugValue = rawSlug.replace(/[!]+$/, "").trim()
                                                        const fieldLabel = entry.matchingField || "field"
                                                        const valueLabel = entry.matchingValue || ""
                                                        const key = `${slugValue}:${fieldLabel}`
                                                        return (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                onClick={() => {
                                                                    if (!slugValue) return
                                                                    if (!confirmLeaveItemEditorIfNeeded()) return
                                                                    const highlightUid = entry.ref || entry.item?.uid || null
                                                                    setIsCollectionSearchOpen(false)
                                                                    setCollectionSearchTerm("")
                                                                    setSelectedIndex(-1)
                                                                    navigate(buildCollectionPath({ slug: slugValue }), {
                                                                        state: buildCollectionSearchNavigationState({
                                                                            highlightUid,
                                                                            matchingField: entry.matchingField,
                                                                            matchingValue: entry.matchingValue,
                                                                            searchQuery: collectionSearchTerm,
                                                                        }),
                                                                    })
                                                                }}
                                                                ref={itemSelectedIndex === flatIndex ? highlightedItemRef : undefined}
                                                                className={`w-full flex flex-col px-3 py-1.5 rounded-md text-left hover:bg-gray-100 dark:hover:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 ${itemSelectedIndex === flatIndex
                                                                    ? "bg-gray-100 dark:bg-gray-800"
                                                                    : ""
                                                                    }`}
                                                            >
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="truncate font-medium">
                                                                        {col.title || col.slug || col.collection}
                                                                    </span>
                                                                </div>
                                                                <div className="text-[0.7rem] text-gray-500 dark:text-gray-400 truncate">
                                                                    {fieldLabel}
                                                                    {valueLabel && `: ${valueLabel}`}
                                                                </div>
                                                            </button>
                                                        )
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {componentsLoading && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Loading collections...
                                </p>
                            )}
                            {componentsError && !componentsLoading && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {typeof componentsError === "string"
                                            ? componentsError
                                            : "Failed to load collections"}
                                    </AlertDescription>
                                </Alert>
                            )}
                            {!componentsLoading && !componentsError && !collectionSearchTerm && filteredCollections.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    No collections found.
                                </p>
                            )}
                            {!componentsLoading && filteredCollections.length > 0 && (
                                <div className="max-h-80 overflow-y-auto space-y-1">
                                    {filteredCollections.map((c: any, index: number) => {
                                        const isSelected = index === selectedIndex || (selectedIndex === -1 && filteredCollections.length === 1 && index === 0)
                                        const creatorBadge = getCollectionCreatorBadgeCopy(c)
                                        return (
                                            <button
                                                key={c.uid}
                                                type="button"
                                                onClick={() => navigateToCollectionAtIndex(index)}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left hover:bg-gray-100 dark:hover:bg-gray-800 ${isSelected ? "bg-gray-100 dark:bg-gray-800" : ""}`}
                                            >
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <Database className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                                            {c.name || c.slug}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {c.slug}
                                                        </div>
                                                    </div>
                                                    {creatorBadge ? (
                                                        creatorBadge.isLabwickAdmin ? (
                                                            <span
                                                                className="inline-flex items-center justify-center shrink-0"
                                                                title="By labwick admin"
                                                                aria-label="By labwick admin"
                                                            >
                                                                <User className="h-3.5 w-3.5 text-amber-500" />
                                                            </span>
                                                        ) : creatorBadge.isUnknownUser ? (
                                                            <span
                                                                className="inline-flex items-center justify-center shrink-0"
                                                                title={creatorBadge.title}
                                                                aria-label={creatorBadge.title}
                                                            >
                                                                <Dot className="h-4 w-4 fill-amber-500 stroke-none text-amber-500" />
                                                            </span>
                                                        ) : (
                                                            <Badge
                                                                variant="secondary"
                                                                className="text-[0.65rem] font-normal shrink-0 max-w-[8rem] truncate border-amber-200/80 text-amber-950 bg-amber-50 dark:border-amber-700 dark:text-amber-100 dark:bg-amber-950/50"
                                                                title={creatorBadge.title}
                                                            >
                                                                {creatorBadge.text}
                                                            </Badge>
                                                        )
                                                    ) : null}
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
                <QuickAddDialog
                    open={isQuickAddOpen}
                    onOpenChange={setIsQuickAddOpen}
                    onSavedToInbox={() => {
                        window.dispatchEvent(new CustomEvent("inbox-updated"))
                    }}
                    onSavedToCollection={() => {
                        window.dispatchEvent(new CustomEvent("inbox-updated"))
                    }}
                />
                {isHomeRoute && (
                    <div className="fixed bottom-2 right-4 text-[11px] text-gray-400 dark:text-gray-500 pointer-events-none select-none">
                        <span>
                            {[
                                appVersion
                                    ? `v ${appVersion}${appReleaseDate ? ` · ${appReleaseDate}` : ""}`
                                    : "",
                                env.VITE_NODE_ENV || env.MODE || "development",
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                    </div>
                )}
            </div>
        </ThemeProvider>
    )
}

export default FullScreenComponent
